package com.rosario.juridico.navigation

import io.ktor.http.decodeURLQueryComponent

import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.navigation.NavHostController
import androidx.navigation.NavType
import androidx.navigation.compose.NavHost
import androidx.navigation.compose.composable
import androidx.navigation.navArgument
import com.rosario.juridico.ui.screens.*
import com.rosario.juridico.marketplace.ui.*
import com.rosario.juridico.marketplace.viewmodel.MarketplaceViewModel
import com.rosario.juridico.marketplace.billing.getBillingProvider
import com.rosario.juridico.PantallaLiquidadorDeuda
import com.rosario.juridico.ui.screens.PantallaPagoExitoso

import com.rosario.juridico.marketplace.viewmodel.ConsultasFeedViewModel
import com.rosario.juridico.marketplace.viewmodel.DirectorioViewModel

@Composable
fun JuridicoNavGraph(
    navController: NavHostController,
    startDestination: String,
    modifier: Modifier = Modifier,
    marketplaceViewModel: MarketplaceViewModel = remember { MarketplaceViewModel(getBillingProvider()) },
    directorioViewModel: DirectorioViewModel = remember { DirectorioViewModel() },
    consultasFeedViewModel: ConsultasFeedViewModel = remember { ConsultasFeedViewModel() },
    onExitRequested: () -> Unit = {},
    onOpenDrawer: () -> Unit = {}
) {
    NavHost(
        navController      = navController,
        startDestination   = startDestination,
        modifier           = modifier,
        enterTransition    = { fadeIn(animationSpec = tween(400, delayMillis = 50)) },
        exitTransition     = { fadeOut(animationSpec = tween(400)) },
        popEnterTransition = { fadeIn(animationSpec = tween(400)) },
        popExitTransition  = { fadeOut(animationSpec = tween(400)) }
    ) {

        // ── Principal ──────────────────────────────────────────────
        composable(Routes.INICIO) {
            PantallaInicio(
                navController    = navController,
                viewModel        = marketplaceViewModel,
                onShowExitDialog = onExitRequested,
                onOpenDrawer     = onOpenDrawer
            )
        }
        
        composable(
            route = Routes.LOGIN,
            arguments = listOf(
                navArgument("register") { 
                    type = NavType.BoolType
                    defaultValue = false 
                }
            )
        ) { backStackEntry ->
            val initialRegisterMode = backStackEntry.arguments?.getBoolean("register") ?: false
            PantallaLogin(navController = navController, initialRegisterMode = initialRegisterMode)
        }

        // ── IA ─────────────────────────────────────────────────────
        composable(
            route = "consulta?query={query}",
            arguments = listOf(
                navArgument("query") {
                    type = NavType.StringType
                    nullable = true
                    defaultValue = null
                }
            )
        ) { backStackEntry ->
            val rawQuery = backStackEntry.arguments?.getString("query")
            val query = rawQuery?.let {
                try {
                    it.decodeURLQueryComponent()
                } catch (_: Exception) {
                    it
                }
            }
            PantallaConsulta(navController = navController, initialQuery = query)
        }
        composable(Routes.ASISTENTE_REDACCION) {
            PantallaAsistenteRedaccion()
        }

        // ── Calculadoras ───────────────────────────────────────────
        composable(Routes.CALCULADORA_DIAS) {
            PantallaCalculadoraDias(navController = navController)
        }
        composable(Routes.CALCULADORA_VENCIMIENTOS) {
            PantallaCalculadoraVencimientos(navController = navController)
        }
        composable(Routes.CALCULADORA_HONORARIOS) {
            PantallaCalculadoraHonorarios(navController = navController)
        }
        composable(Routes.CALCULADORA_INCAPACIDAD) {
            PantallaCalculadoraIncapacidad(navController = navController)
        }
        
        composable("liquidador_deuda") {
            PantallaLiquidadorDeuda(navController = navController)
        }

        composable("codigos_sellados") {
            // Llamada posicional para resolver el error de resolución del compilador
            PantallaCodigosSellados(navController)
        }

        // ── Documentos ─────────────────────────────────────────────
        composable(Routes.DOCUMENTOS) {
            PantallaDocumentos(navController = navController)
        }
        composable(Routes.MODELOS_ALQUILER) {
            PantallaModelosAlquiler(navController = navController)
        }
        composable(
            route = Routes.GUIA_INFO,
            arguments = listOf(navArgument("focusId") { type = NavType.StringType; nullable = true })
        ) { backStackEntry ->
            val focusId = backStackEntry.arguments?.getString("focusId")
            PantallaGuiaInfo(focusId = focusId)
        }
        composable(Routes.VISOR_PDF) { backStackEntry ->
            val titulo = backStackEntry.arguments?.getString("titulo") ?: ""
            val resId  = backStackEntry.arguments?.getString("resId") ?: ""
            PantallaContenidoDocumento(
                titulo = titulo,
                resId  = resId
            )
        }

        // ── Generador de contratos ─────────────────────────────────
        composable(Routes.GENERADOR_CONTRATO_CON_CLAVE) { backStackEntry ->
            val clave = backStackEntry.arguments?.getString("clave")
            PantallaGeneradorDeContratoAlquiler(navController = navController, clave = clave)
        }
        composable(Routes.GENERADOR_CONTRATO) {
            PantallaGeneradorDeContratoAlquiler(navController = navController, clave = null)
        }

        // ── Marketplace (Protegido por Auth Guard) ─────────────────
        protectedMarketplaceComposable(
            route = Routes.REGISTRO_ABOGADO,
            navController = navController
        ) {
            OnboardingRolScreen(
                navController = navController,
                viewModel     = marketplaceViewModel,
                role          = "abogado"
            )
        }
        protectedMarketplaceComposable(
            route = Routes.PUBLICAR_CONSULTA,
            navController = navController
        ) {
            PublicarConsultaScreen(
                navController = navController,
                viewModel     = marketplaceViewModel,
                feedViewModel = consultasFeedViewModel
            )
        }
        protectedMarketplaceComposable(
            route = Routes.LISTADO_CONSULTAS,
            navController = navController
        ) {
            ListadoConsultasScreen(
                navController = navController,
                viewModel     = marketplaceViewModel,
                feedViewModel = consultasFeedViewModel
            )
        }
        composable(Routes.DIRECTORIO_AVVO) {
            DirectorioAvvoScreen(
                navController = navController,
                viewModel     = directorioViewModel
            )
        }
        protectedMarketplaceComposable(
            route = Routes.DETALLE_CONSULTA,
            arguments = listOf(
                navArgument("consultaId") { type = NavType.StringType }
            ),
            navController = navController
        ) { backStackEntry ->
            val consultaId = backStackEntry.arguments?.getString("consultaId") ?: ""
            DetalleConsultaScreen(
                consultaId    = consultaId,
                navController = navController,
                viewModel     = marketplaceViewModel
            )
        }
        protectedMarketplaceComposable(
            route = Routes.CHAT,
            arguments = listOf(
                navArgument("consultaId") { type = NavType.StringType }
            ),
            navController = navController
        ) { backStackEntry ->
            val consultaId = backStackEntry.arguments?.getString("consultaId") ?: ""
            ChatScreen(
                consultaId    = consultaId,
                navController = navController
            )
        }
        protectedMarketplaceComposable(
            route = Routes.PERFIL_ABOGADO,
            navController = navController
        ) {
            PerfilAbogadoScreen(
                abogadoId     = "current",
                navController = navController,
                viewModel     = marketplaceViewModel
            )
        }
        protectedMarketplaceComposable(
            route = "mis_consultas",
            navController = navController
        ) {
            MisConsultasScreen(
                navController = navController,
                viewModel     = marketplaceViewModel
            )
        }
        protectedMarketplaceComposable(
            route = "detalle_mis_ofertas/{consultaId}",
            arguments = listOf(
                navArgument("consultaId") { type = NavType.StringType }
            ),
            navController = navController
        ) { backStackEntry ->
            val consultaId = backStackEntry.arguments?.getString("consultaId") ?: ""
            DetalleMisOfertasScreen(
                consultaId    = consultaId,
                navController = navController,
                viewModel     = marketplaceViewModel
            )
        }
        composable(
            route = "onboarding_rol?role={role}&editMode={editMode}",
            arguments = listOf(
                navArgument("role") {
                    type = NavType.StringType
                    nullable = true
                    defaultValue = null
                },
                navArgument("editMode") {
                    type = NavType.BoolType
                    defaultValue = false
                }
            )
        ) { backStackEntry ->
            val role = backStackEntry.arguments?.getString("role")
            val editMode = backStackEntry.arguments?.getBoolean("editMode") ?: false
            OnboardingRolScreen(
                navController = navController,
                viewModel = marketplaceViewModel,
                role = role,
                editMode = editMode
            )
        }
        composable(Routes.CALCULADORA_ECONOMICA) {
            PantallaCalculadoraEconomica(navController = navController)
        }
        composable(Routes.PLANILLA_MEDIACION) {
            PantallaPlanillaCierreMediacion(navController = navController)
        }
        composable(Routes.INFO_SCREEN) {
            PantallaInfo()
        }
        composable(Routes.PANEL_ADMIN) {
            PanelAdminScreen(
                navController = navController,
                viewModel = marketplaceViewModel,
                directorioViewModel = directorioViewModel
            )
        }

        // ── Agenda de Expedientes ──────────────────────────────────
        composable(Routes.AGENDA_EXPEDIENTES) {
            AgendaExpedientesScreen(navController = navController)
        }
        composable(Routes.AGENDA_PROMESA) {
            AgendaPromesaScreen(navController = navController)
        }
        composable(Routes.NUEVO_EXPEDIENTE) {
            PantallaNuevoExpediente(navController = navController)
        }
        composable(Routes.DETALLE_EXPEDIENTE) { backStackEntry ->
            val expedienteId = backStackEntry.arguments?.getString("expedienteId") ?: ""
            PantallaDetalleExpediente(
                expedienteId  = expedienteId,
                navController = navController
            )
        }

        // ── Premium y Pagos ────────────────────────────────────────
        composable(Routes.PAYWALL) {
            PantallaPaywall(navController = navController)
        }

        composable(Routes.PAYMENT_SUCCESS) {
            PantallaPagoExitoso(
                onContinue = {
                    navController.navigate(Routes.INICIO) {
                        popUpTo(Routes.INICIO) { inclusive = true }
                    }
                }
            )
        }
    }
}
